import { is } from '../util/is';
import { reduce } from '../collections/reduce';

type Filterable<T> = T[] | Record<string, T>;

type Predicate<A> = (a: A, keyOrIndex?: number | string) => boolean;

/**
 * Takes a [[Predicate]] and iterate over a [[Filterable]] and return a new `Filterable` containing all elements of the original which satisfy the `Predicate`.
 *
 * ```ts
 * tt.filter(n => n % 2 === 1, [1, 2, 3]);
 * // => [1, 3]
 * ```
 *
 * @param fn The predicate to test.
 */
export function filter<A>(fn: Predicate<A>, f: A[]): A[];
export function filter<A>(fn: Predicate<A>, f: Record<string, A>): Record<string, A>;
export function filter<A>(fn: Predicate<A>, f: Filterable<A>): Filterable<A> {
  if (is(Array, f)) {
    return reduce<A[], A>(
      (acc, x, i) => {
        if (fn(x, i)) {
          acc.push(x);
        }
        return acc;
      },
      [],
      f
    );
  } else {
    return reduce(
      (acc, x, k) => {
        if (fn(x, k)) {
          acc[k] = x;
        }
        return acc;
      },
      {} as Record<string, A>,
      f
    );
  }
}
